/**
 * corporateSlice - Redux slice for corporate authentication and state management
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CorporateAuthService, { Corporate } from '../core/auth/CorporateAuthService';
import TokenManager from '../core/auth/TokenManager';

interface CorporateState {
  corporate: Corporate | null;
  role: 'admin' | 'hr_manager' | 'finance' | 'viewer' | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: CorporateState = {
  corporate: localStorage.getItem('corporate') ? JSON.parse(localStorage.getItem('corporate')!) : null,
  role: (localStorage.getItem('corporateRole') as any) || null,
  isAuthenticated: TokenManager.hasValidToken(),
  isLoading: false,
  error: null
};

/**
 * Async thunk for corporate login
 */
export const loginCorporate = createAsyncThunk(
  'corporate/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await CorporateAuthService.loginCorporate(credentials);
      TokenManager.setTokens({
        accessToken: response.token,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn || 900,
        tokenType: 'Bearer'
      });
      localStorage.setItem('corporate', JSON.stringify(response.corporate));
      localStorage.setItem('corporateRole', response.role);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Corporate login failed');
    }
  }
);

/**
 * Async thunk for Google SSO login
 */
export const loginWithGoogle = createAsyncThunk(
  'corporate/loginGoogle',
  async (idToken: string, { rejectWithValue }) => {
    try {
      const response = await CorporateAuthService.loginWithGoogle(idToken);
      TokenManager.setTokens({
        accessToken: response.token,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn || 900,
        tokenType: 'Bearer'
      });
      localStorage.setItem('corporate', JSON.stringify(response.corporate));
      localStorage.setItem('corporateRole', response.role);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Google login failed');
    }
  }
);

/**
 * Async thunk for Microsoft SSO login
 */
export const loginWithMicrosoft = createAsyncThunk(
  'corporate/loginMicrosoft',
  async (idToken: string, { rejectWithValue }) => {
    try {
      const response = await CorporateAuthService.loginWithMicrosoft(idToken);
      TokenManager.setTokens({
        accessToken: response.token,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn || 900,
        tokenType: 'Bearer'
      });
      localStorage.setItem('corporate', JSON.stringify(response.corporate));
      localStorage.setItem('corporateRole', response.role);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Microsoft login failed');
    }
  }
);

/**
 * Async thunk for fetching corporate profile
 */
export const fetchCorporateProfile = createAsyncThunk(
  'corporate/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const profile = await CorporateAuthService.getCorporateProfile();
      localStorage.setItem('corporate', JSON.stringify(profile));
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch profile');
    }
  }
);

/**
 * Async thunk for updating corporate profile
 */
export const updateCorporateProfile = createAsyncThunk(
  'corporate/updateProfile',
  async (data: Partial<Corporate>, { rejectWithValue }) => {
    try {
      const response = await CorporateAuthService.updateCorporateProfile(data);
      localStorage.setItem('corporate', JSON.stringify(response.corporate));
      return response.corporate;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

const corporateSlice = createSlice({
  name: 'corporate',
  initialState,
  reducers: {
    logout: (state) => {
      state.corporate = null;
      state.role = null;
      state.isAuthenticated = false;
      state.error = null;
      TokenManager.clearAllTokens();
      localStorage.removeItem('corporate');
      localStorage.removeItem('corporateRole');
      CorporateAuthService.logout();
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginCorporate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginCorporate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.corporate = action.payload.corporate;
        state.role = action.payload.role;
      })
      .addCase(loginCorporate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Google Login
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.corporate = action.payload.corporate;
        state.role = action.payload.role;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Microsoft Login
    builder
      .addCase(loginWithMicrosoft.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithMicrosoft.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.corporate = action.payload.corporate;
        state.role = action.payload.role;
      })
      .addCase(loginWithMicrosoft.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Profile
    builder
      .addCase(fetchCorporateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCorporateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.corporate = action.payload;
      })
      .addCase(fetchCorporateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Profile
    builder
      .addCase(updateCorporateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCorporateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.corporate = action.payload;
      })
      .addCase(updateCorporateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { logout, clearError } = corporateSlice.actions;
export default corporateSlice.reducer;
